import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

/**
 * Engine-Version Impact Tile.
 *
 * Calls GET /api/admin/trade-impact-by-version and renders a compact
 * per-version expectancy table so we can answer "did this fix actually
 * move the needle?" with real data, not gut feel.
 *
 * Optional notional filter (1-row of pills) so the analysis isn't
 * confounded by position-size scale-ups — the most common source of
 * "looks like the bot is degrading" false alarms.
 */
const NTL_BUCKETS = [
  { key: 'all',     label: 'All sizes', min: null, max: null },
  { key: 'micro',   label: '≤$25',     min: 0,    max: 25 },
  { key: 'small',   label: '$25–$50',  min: 25,   max: 50 },
  { key: 'medium',  label: '$50–$100', min: 50,   max: 100 },
  { key: 'large',   label: '>$100',    min: 100,  max: null },
];

const fmtUsd = (n) => {
  const v = Number(n) || 0;
  const sign = v >= 0 ? '+' : '−';
  return `${sign}$${Math.abs(v).toFixed(3)}`;
};
const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export default function EngineImpactPanel() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState('all');

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const b = NTL_BUCKETS.find((x) => x.key === bucket) || NTL_BUCKETS[0];
      const params = {};
      if (b.min !== null) params.notional_min = b.min;
      if (b.max !== null) params.notional_max = b.max;
      const resp = await axios.get(`${API}/admin/trade-impact-by-version`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        timeout: 15000,
      });
      setData(resp.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [bucket]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.versions || []).slice().reverse(); // newest first
  const currentVersion = data?.current_version;

  return (
    <div
      data-testid="engine-impact-panel"
      className="bg-gray-800 border border-gray-700 rounded-lg p-4 mt-6"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-white">Engine Impact by Version</h3>
          <p className="text-xs text-gray-400">
            Live trade expectancy grouped by engine code version. Use the size filter to
            remove notional-scale confounds.
            {currentVersion && (
              <span className="ml-2 text-blue-300 font-mono">current: v{currentVersion}</span>
            )}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded"
          data-testid="engine-impact-refresh"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Notional filter pills */}
      <div className="flex flex-wrap gap-2 mb-3">
        {NTL_BUCKETS.map((b) => (
          <button
            key={b.key}
            onClick={() => setBucket(b.key)}
            data-testid={`engine-impact-bucket-${b.key}`}
            className={`text-xs px-2.5 py-1 rounded border transition-colors ${
              bucket === b.key
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-2 text-red-300 text-xs bg-red-900/30 border border-red-700/50 rounded p-2">
          {error}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-xs text-gray-400 italic py-4 text-center">
          No tagged trades yet in this size bucket. New trades will start showing here as the
          engine records round-trips.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-gray-200" data-testid="engine-impact-table">
            <thead className="text-gray-400 text-[10px] uppercase tracking-wider border-b border-gray-700">
              <tr>
                <th className="text-left py-2 px-2">Version</th>
                <th className="text-right py-2 px-2">Trips</th>
                <th className="text-right py-2 px-2">Win %</th>
                <th className="text-right py-2 px-2">Avg Win</th>
                <th className="text-right py-2 px-2">Avg Loss</th>
                <th className="text-right py-2 px-2">Max Loss</th>
                <th className="text-right py-2 px-2">EV/Trip</th>
                <th className="text-right py-2 px-2">Net P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const netClass = r.net_pnl >= 0 ? 'text-green-400' : 'text-red-400';
                const evClass  = r.expectancy_per_trip >= 0 ? 'text-green-400' : 'text-red-400';
                return (
                  <tr
                    key={r.version}
                    className="border-b border-gray-800 hover:bg-gray-900/50"
                    data-testid={`engine-impact-row-${r.version}`}
                  >
                    <td className="py-1.5 px-2 font-mono">
                      <div className="flex items-center gap-2">
                        <span>{r.version}</span>
                        {r.is_current && (
                          <span className="text-[9px] bg-blue-600 text-white rounded px-1.5 py-0.5">LIVE</span>
                        )}
                      </div>
                      {r.change_log?.id && (
                        <div className="text-[10px] text-gray-500 mt-0.5 max-w-[260px] truncate" title={r.change_log.description}>
                          {r.change_log.id}
                        </div>
                      )}
                    </td>
                    <td className="text-right py-1.5 px-2 font-mono">{r.trips}</td>
                    <td className="text-right py-1.5 px-2 font-mono">{pct(r.win_rate_pct)}</td>
                    <td className="text-right py-1.5 px-2 font-mono text-green-400">{fmtUsd(r.avg_win)}</td>
                    <td className="text-right py-1.5 px-2 font-mono text-red-400">{fmtUsd(r.avg_loss)}</td>
                    <td className="text-right py-1.5 px-2 font-mono text-red-400">{fmtUsd(r.max_loss)}</td>
                    <td className={`text-right py-1.5 px-2 font-mono ${evClass}`}>{fmtUsd(r.expectancy_per_trip)}</td>
                    <td className={`text-right py-1.5 px-2 font-mono font-semibold ${netClass}`}>{fmtUsd(r.net_pnl)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Change log accordion — hypothesis + description per version */}
      {data?.change_log?.length > 0 && (
        <details className="mt-3 text-xs text-gray-300">
          <summary className="cursor-pointer text-gray-400 hover:text-white">
            Change log ({data.change_log.length} entries) — show hypotheses
          </summary>
          <div className="mt-2 space-y-2 pl-2 border-l border-gray-700">
            {data.change_log.map((c) => (
              <div key={c.version} className="pl-2">
                <div className="font-mono text-blue-300">
                  v{c.version} <span className="text-gray-500">· {c.date} · {c.id}</span>
                </div>
                <div className="text-gray-200 mt-0.5"><strong>Hypothesis:</strong> {c.hypothesis}</div>
                <div className="text-gray-400 mt-0.5">{c.description}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
